import React, { useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import shave from 'shave';
import { integer } from 'airbnb-prop-types';
import Popup from '../Popup/Popup';
import crossIcon from '../../images/cross-icon.svg';
import {
    registerResizeHandler,
    unregisterResizeHandler
} from '../../utils/resize-handler';

import styles from './TruncatedDisclaimer.css';

const popUpStyle = () => {
    const styling = {
        borderRadius: '8px',
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
        padding: '16px'
    };

    if (window.innerWidth <= 768) {
        Object.assign(
            styling,
            { width: '100%' },
            { overflowY: 'auto' },
            { maxHeight: '65vh' }
        );
    }

    return styling;
};

const noop = () => {};

const TruncatedDisclaimer = ({
    title,
    children,
    maxLines,
    fundScreener = false
}) => {
    const [modalOpen, setModalOpen] = useState(false);

    const disclaimerRef = React.createRef();
    useEffect(() => {
        const { current: wrappingEl } = disclaimerRef;

        if (!wrappingEl) return noop;

        const element = wrappingEl.firstChild
            ? wrappingEl.firstChild
            : wrappingEl;

        // If element is not of type Element, "parameter is not of type" error is bubbling up in storybook
        const computedStyle =
            element instanceof Element
                ? getComputedStyle(
                      wrappingEl.firstChild ? wrappingEl.firstChild : wrappingEl
                  )
                : null;

        if (!computedStyle) return noop;

        const { lineHeight } = computedStyle;

        const maxHeight = maxLines * parseFloat(lineHeight);

        const shaveIt = debounce(() => shave(wrappingEl, maxHeight), {
            leading: true
        });
        registerResizeHandler(shaveIt);
        shaveIt();
        return () => unregisterResizeHandler(shaveIt);
    });

    const closeModal = () => setModalOpen(false);
    const openModal = () => setModalOpen(true);
    return (
        <React.Fragment>
            <div className={fundScreener && 'body'}>
                {title && (
                    <p
                        className={
                            fundScreener
                                ? `disclaimer-title-fund-screener`
                                : `disclaimer-title`
                        }
                    >
                        {fundScreener && '*'}
                        {title}
                    </p>
                )}
                <div className="content" ref={disclaimerRef}>
                    {children}
                </div>
                <button
                    className={
                        fundScreener
                            ? 'read-more-fund-screener'
                            : 'read-more-details'
                    }
                    onClick={openModal}
                    type="button"
                >
                    Read More
                </button>
                <Popup
                    open={modalOpen}
                    closeOnDocumentClick
                    closeOnEscape
                    onClose={closeModal}
                    contentStyle={popUpStyle()}
                >
                    <button
                        className="close-btn"
                        style={{ float: 'right' }}
                        onClick={() => closeModal()}
                        type="button"
                    >
                        <img
                            className="close-btn-img"
                            alt="Close"
                            src={crossIcon}
                        />
                    </button>
                    {title && <h2>{title}</h2>}
                    {children}
                </Popup>

                <style jsx>{styles}</style>
            </div>
        </React.Fragment>
    );
};

TruncatedDisclaimer.defaultProps = {
    /**
     * If no children are passed, render empty div
     */
    children: <div />,

    /**
     * If not specified max 3 lines
     */
    maxLines: 3,

    /**
     * If no title false
     */
    title: false
};

TruncatedDisclaimer.propTypes = {
    /**
     * Child component should contain a node
     */
    children: PropTypes.node,

    /**
     * Child component should contain a node
     */
    maxLines: integer(),

    /**
     * Child component should contain a node
     */
    title: PropTypes.node
};

export default TruncatedDisclaimer;
